import {
  ALL_OPPORTUNITY_TYPES_ENABLED,
  EMPTY_MARKETPLACE_ORDER_HANDOFF_DEFAULTS,
  MARKETPLACE_ORDER_HANDOFF_DEFAULT_ITEMS,
  marketEntryKey,
  sameEnabledOpportunityTypes,
  type AddPreflightMarketInput,
  type CurrencyCode,
  type EnabledOpportunityTypes,
  type IndustryDefaultBrandUpdate,
  type MarketplaceOrderHandoffDefaults,
} from "@nexus/contracts";

import { handoffDefaultsEqual } from "./marketplace-handoff-sync";
import { sameEmails, savedInboxEmails } from "./marketplace-inbox-rows";

export const TENANT_PRODUCT_LINES = ["CORE", "COMP", "CORE_AND_COMP"] as const;
export type TenantProductLine = (typeof TENANT_PRODUCT_LINES)[number];
export const DEFAULT_TENANT_PRODUCT_LINE: TenantProductLine = "CORE_AND_COMP";

export function resolveTenantProductLine(value: string | null | undefined): TenantProductLine {
  if (TENANT_PRODUCT_LINES.includes(value as TenantProductLine)) return value as TenantProductLine;
  return DEFAULT_TENANT_PRODUCT_LINE;
}

/** Trimmed empty string, null, and undefined all mean "no value". */
export function emptyToNull(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? "";
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Default industry to persist: keep the form value if it is still assigned.
 * Do not infer a default from a single remaining industry — that is not a user edit.
 */
export function nextDefaultIndustry(
  selected: string | null | undefined,
  industryIndices: string[],
): string | null {
  const value = emptyToNull(selected);
  if (value && industryIndices.includes(value)) return value;
  return null;
}

/** Industry index → admin-set default My Brand. Absent or empty means "no default". */
export type IndustryDefaultBrandMap = Record<string, string>;

export function toIndustryDefaultBrandMap(
  rows: ReadonlyArray<{ industry: string; defaultBrand: string | null }> | null | undefined,
): IndustryDefaultBrandMap {
  const map: IndustryDefaultBrandMap = {};
  for (const row of rows ?? []) {
    if (row.defaultBrand) map[row.industry] = row.defaultBrand;
  }
  return map;
}

/** Changed defaults for the assigned industries only; unassigned industries keep theirs. */
export function industryDefaultBrandChanges(
  form: IndustryDefaultBrandMap,
  saved: IndustryDefaultBrandMap,
  industryIndices: string[],
): IndustryDefaultBrandUpdate[] {
  return industryIndices
    .map((industry) => ({ industry, defaultBrand: emptyToNull(form[industry]) }))
    .filter(({ industry, defaultBrand }) => defaultBrand !== emptyToNull(saved[industry]));
}

export interface OrgSettingsDirtyOrg {
  name: string;
  description?: string | null;
  brandColor?: string | null;
  type: string;
  productLine?: string | null;
  retoolUserId?: string | null;
  industryIndices?: string[];
}

export interface OrgSettingsDirtyAdmin {
  defaultIndustry?: string | null;
  defaultBrand?: string | null;
  customerPartition?: string | null;
  conversionsMonthly?: number;
  conversionsWeekly?: number;
  currency?: CurrencyCode;
  marketplaceOrdersInboxEmails?: string[];
  marketplaceOrdersInboxEmail?: string | null;
  marketplaceOrderHandoffDefaults?: MarketplaceOrderHandoffDefaults | null;
  industryDefaultBrands?: Array<{ industry: string; defaultBrand: string | null }>;
  bqSyncFailureSlackAlertEnabled?: boolean;
  enabledOpportunityTypes?: EnabledOpportunityTypes;
  /** Saved non-Global Pre Flight markets. Undefined while loading, so markets never read as changed. */
  preflightMarketRefs?: AddPreflightMarketInput[];
}

export interface OrgSettingsDirtyForm {
  name: string;
  description: string;
  brandColor: string | null;
  type: string;
  productLine: TenantProductLine;
  retoolUserId: string;
  industryIndices: string[];
  logoUploadId: string | null;
  logoRemoved: boolean;
  defaultIndustry: string | null;
  defaultBrand: string;
  industryDefaultBrands: IndustryDefaultBrandMap;
  customerPartition: string;
  inboxEmails: string[];
  handoffDefaults: MarketplaceOrderHandoffDefaults;
  conversionsMonthly: number;
  conversionsWeekly: number;
  currency: CurrencyCode;
  bqSyncFailureSlackAlertEnabled: boolean;
  enabledOpportunityTypes: EnabledOpportunityTypes;
  preflightMarketRefs: AddPreflightMarketInput[];
}

export interface OrgSettingsTenantChanges {
  name?: string;
  description?: string | null;
  brandColor?: string | null;
  type?: string;
  productLine?: TenantProductLine;
  retoolUserId?: string;
  industryIndices?: string[];
  logoUploadId?: string | null;
  logoUrl?: null;
}

export interface OrgSettingsSettingsChanges {
  defaultIndustry?: string | null;
  defaultBrand?: string | null;
  customerPartition?: string | null;
  conversionsMonthly?: number;
  conversionsWeekly?: number;
  currency?: CurrencyCode;
  marketplaceOrdersInboxEmails?: string[];
  marketplaceOrderHandoffDefaults?: MarketplaceOrderHandoffDefaults;
  industryDefaultBrands?: IndustryDefaultBrandUpdate[];
  bqSyncFailureSlackAlertEnabled?: boolean;
  enabledOpportunityTypes?: EnabledOpportunityTypes;
}

export interface OrgSettingsMarketChanges {
  preflightMarketRefs?: AddPreflightMarketInput[];
}

function sameMarketRefs(a: AddPreflightMarketInput[], b: AddPreflightMarketInput[]): boolean {
  const keys = new Set(a.map(marketEntryKey));
  return a.length === b.length && b.every((ref) => keys.has(marketEntryKey(ref)));
}

function normalizeHandoff(
  raw: MarketplaceOrderHandoffDefaults | null | undefined,
): MarketplaceOrderHandoffDefaults {
  if (!raw) return { ...EMPTY_MARKETPLACE_ORDER_HANDOFF_DEFAULTS };
  return { ...EMPTY_MARKETPLACE_ORDER_HANDOFF_DEFAULTS, ...raw };
}

export function orgSettingsChanges(
  form: OrgSettingsDirtyForm,
  org: OrgSettingsDirtyOrg,
  admin: OrgSettingsDirtyAdmin | null | undefined,
): {
  tenantChanges: OrgSettingsTenantChanges;
  settingsChanges: OrgSettingsSettingsChanges;
  marketChanges: OrgSettingsMarketChanges;
} {
  const tenantChanges: OrgSettingsTenantChanges = {};
  const settingsChanges: OrgSettingsSettingsChanges = {};
  const marketChanges: OrgSettingsMarketChanges = {};

  const trimmedName = form.name.trim();
  if (trimmedName !== org.name) tenantChanges.name = trimmedName;

  const nextDescription = emptyToNull(form.description);
  if (nextDescription !== emptyToNull(org.description)) tenantChanges.description = nextDescription;

  const currentBrandColor = org.brandColor?.trim() ?? null;
  if (form.brandColor !== currentBrandColor) tenantChanges.brandColor = form.brandColor;

  const currentType = org.type === "API" ? "API" : "ORGANIZATION";
  if (form.type !== currentType) tenantChanges.type = form.type;

  const currentProductLine = resolveTenantProductLine(org.productLine);
  if (form.productLine !== currentProductLine) tenantChanges.productLine = form.productLine;

  const currentRetoolUserId = org.retoolUserId ?? "";
  if (form.retoolUserId && form.retoolUserId !== currentRetoolUserId) {
    tenantChanges.retoolUserId = form.retoolUserId;
  }

  const prevIndices = [...(org.industryIndices ?? [])].sort().join(",");
  const nextIndices = [...form.industryIndices].sort().join(",");
  if (nextIndices !== prevIndices) tenantChanges.industryIndices = form.industryIndices;

  if (form.logoUploadId !== null) tenantChanges.logoUploadId = form.logoUploadId;
  else if (form.logoRemoved) {
    tenantChanges.logoUrl = null;
    tenantChanges.logoUploadId = null;
  }

  const nextDefault = nextDefaultIndustry(form.defaultIndustry, form.industryIndices);
  if (nextDefault !== emptyToNull(admin?.defaultIndustry)) {
    settingsChanges.defaultIndustry = nextDefault;
  }

  const nextBrand = emptyToNull(form.defaultBrand);
  if (nextBrand !== emptyToNull(admin?.defaultBrand)) settingsChanges.defaultBrand = nextBrand;

  const industryDefaults = industryDefaultBrandChanges(
    form.industryDefaultBrands,
    toIndustryDefaultBrandMap(admin?.industryDefaultBrands),
    form.industryIndices,
  );
  if (industryDefaults.length > 0) settingsChanges.industryDefaultBrands = industryDefaults;

  const nextPartition = emptyToNull(form.customerPartition);
  if (nextPartition !== emptyToNull(admin?.customerPartition)) {
    settingsChanges.customerPartition = nextPartition;
  }

  const currentInbox = savedInboxEmails(
    admin?.marketplaceOrdersInboxEmails,
    admin?.marketplaceOrdersInboxEmail,
  );
  if (!sameEmails(form.inboxEmails, currentInbox)) {
    settingsChanges.marketplaceOrdersInboxEmails = form.inboxEmails;
  }

  const savedHandoff = normalizeHandoff(admin?.marketplaceOrderHandoffDefaults);
  if (!handoffDefaultsEqual(form.handoffDefaults, savedHandoff)) {
    settingsChanges.marketplaceOrderHandoffDefaults = form.handoffDefaults;
  }

  if (form.conversionsMonthly !== (admin?.conversionsMonthly ?? 0)) {
    settingsChanges.conversionsMonthly = form.conversionsMonthly;
  }
  if (form.conversionsWeekly !== (admin?.conversionsWeekly ?? 0)) {
    settingsChanges.conversionsWeekly = form.conversionsWeekly;
  }
  if (form.currency !== (admin?.currency ?? "USD")) settingsChanges.currency = form.currency;
  if (form.bqSyncFailureSlackAlertEnabled !== (admin?.bqSyncFailureSlackAlertEnabled ?? true)) {
    settingsChanges.bqSyncFailureSlackAlertEnabled = form.bqSyncFailureSlackAlertEnabled;
  }
  const savedOpportunityTypes = admin?.enabledOpportunityTypes ?? ALL_OPPORTUNITY_TYPES_ENABLED;
  if (!sameEnabledOpportunityTypes(form.enabledOpportunityTypes, savedOpportunityTypes)) {
    settingsChanges.enabledOpportunityTypes = form.enabledOpportunityTypes;
  }

  if (admin?.preflightMarketRefs && !sameMarketRefs(form.preflightMarketRefs, admin.preflightMarketRefs)) {
    marketChanges.preflightMarketRefs = form.preflightMarketRefs;
  }

  return { tenantChanges, settingsChanges, marketChanges };
}

/** One handoff default item, so the footer can name the exact Brand rules / Brand kit field. */
export type OrgSettingsHandoffChangedField = `handoff.${keyof MarketplaceOrderHandoffDefaults}`;

export type OrgSettingsChangedField =
  | Exclude<keyof OrgSettingsTenantChanges, "logoUploadId" | "logoUrl">
  | "logo"
  | Exclude<keyof OrgSettingsSettingsChanges, "marketplaceOrderHandoffDefaults">
  | OrgSettingsHandoffChangedField
  | keyof OrgSettingsMarketChanges;

export function isOrgSettingsHandoffChangedField(
  field: OrgSettingsChangedField,
): field is OrgSettingsHandoffChangedField {
  return field.startsWith("handoff.");
}

export const ORG_SETTINGS_FIELD_TABS = ["general", "organization", "marketplace"] as const;
export type OrgSettingsFieldTab = (typeof ORG_SETTINGS_FIELD_TABS)[number];

/** The org drawer tab that renders each editable field. */
export const ORG_SETTINGS_FIELD_TAB: Record<OrgSettingsChangedField, OrgSettingsFieldTab> = {
  name: "general",
  type: "general",
  logo: "general",
  brandColor: "general",
  description: "general",
  productLine: "organization",
  industryIndices: "organization",
  defaultIndustry: "organization",
  industryDefaultBrands: "organization",
  defaultBrand: "organization",
  preflightMarketRefs: "organization",
  currency: "organization",
  customerPartition: "organization",
  conversionsMonthly: "organization",
  conversionsWeekly: "organization",
  bqSyncFailureSlackAlertEnabled: "organization",
  enabledOpportunityTypes: "organization",
  retoolUserId: "organization",
  marketplaceOrdersInboxEmails: "marketplace",
  "handoff.brandGuidelines": "marketplace",
  "handoff.techSpecs": "marketplace",
  "handoff.policyRestrictions": "marketplace",
  "handoff.dosAndDonts": "marketplace",
  "handoff.productAccuracy": "marketplace",
  "handoff.thumbnailGuidelines": "marketplace",
  "handoff.fontsLicenceProof": "marketplace",
  "handoff.coloursHex": "marketplace",
  "handoff.logos": "marketplace",
};

/** Tabs holding at least one changed field, in tab-strip order. */
export function orgSettingsChangedTabs(fields: OrgSettingsChangedField[]): OrgSettingsFieldTab[] {
  const tabs = new Set(fields.map((field) => ORG_SETTINGS_FIELD_TAB[field]));
  return ORG_SETTINGS_FIELD_TABS.filter((tab) => tabs.has(tab));
}

/** Changed fields in form order; an upload and a removal both count as one "logo" change. */
export function orgSettingsChangedFields(
  form: OrgSettingsDirtyForm,
  org: OrgSettingsDirtyOrg,
  admin: OrgSettingsDirtyAdmin | null | undefined,
): OrgSettingsChangedField[] {
  const { tenantChanges, settingsChanges, marketChanges } = orgSettingsChanges(form, org, admin);
  const fields = new Set<OrgSettingsChangedField>();
  for (const key of Object.keys(tenantChanges) as Array<keyof OrgSettingsTenantChanges>) {
    fields.add(key === "logoUploadId" || key === "logoUrl" ? "logo" : key);
  }
  for (const key of Object.keys(settingsChanges) as Array<keyof OrgSettingsSettingsChanges>) {
    if (key === "marketplaceOrderHandoffDefaults") {
      for (const handoffField of changedHandoffFields(form.handoffDefaults, admin)) fields.add(handoffField);
    } else {
      fields.add(key);
    }
  }
  for (const key of Object.keys(marketChanges) as Array<keyof OrgSettingsMarketChanges>) {
    fields.add(key);
  }
  return [...fields];
}

function changedHandoffFields(
  handoffDefaults: MarketplaceOrderHandoffDefaults,
  admin: OrgSettingsDirtyAdmin | null | undefined,
): OrgSettingsHandoffChangedField[] {
  const saved = normalizeHandoff(admin?.marketplaceOrderHandoffDefaults);
  return MARKETPLACE_ORDER_HANDOFF_DEFAULT_ITEMS.filter(({ key }) => handoffDefaults[key] !== saved[key]).map(
    ({ key }) => `handoff.${key}` as const,
  );
}

export function isOrgSettingsDirty(
  form: OrgSettingsDirtyForm,
  org: OrgSettingsDirtyOrg,
  admin: OrgSettingsDirtyAdmin | null | undefined,
): boolean {
  return orgSettingsChangedFields(form, org, admin).length > 0;
}

/** Wizard-phase new org — any field or step progress beyond a fresh open. */
export interface NewOrgDraftSnapshot {
  phase: "wizard" | "success";
  name: string;
  productLine: TenantProductLine;
  industryIndices: string[];
  preflightMarketCount: number;
  defaultIndustry: string | null;
  defaultBrand: string;
  hasLogo: boolean;
  brandColor: string | null;
  defaultBrandColor: string | null;
  visitedBeyondBasics: boolean;
}

export function isNewOrgDraftDirty(snapshot: NewOrgDraftSnapshot): boolean {
  if (snapshot.phase !== "wizard") return false;
  if (snapshot.visitedBeyondBasics) return true;
  if (snapshot.name.trim().length > 0) return true;
  if (snapshot.productLine !== DEFAULT_TENANT_PRODUCT_LINE) return true;
  if (snapshot.industryIndices.length > 0) return true;
  if (snapshot.preflightMarketCount > 0) return true;
  if (emptyToNull(snapshot.defaultIndustry)) return true;
  if (emptyToNull(snapshot.defaultBrand)) return true;
  if (snapshot.hasLogo) return true;
  if (snapshot.brandColor !== snapshot.defaultBrandColor) return true;
  return false;
}
